/**
 * Generic render/commit error backstop.
 *
 * React error boundaries catch errors thrown during render AND during the
 * commit phase (where host DOM mutations happen). The latter is the case we
 * care about here: browser auto-translation (Google Translate / Edge) rewrites
 * text nodes out from under React, so when React later reconciles a data-driven
 * subtree it can try to `removeChild` a node the translator already replaced —
 * surfacing as "Failed to execute 'removeChild' on 'Node'". Without a boundary
 * that fault escapes to the router's default overlay and white-screens the app.
 *
 * `resetKeys` clears the error state when any key changes (we pass the route
 * pathname), so navigating away from a broken view recovers automatically; the
 * inline "Försök igen" button covers staying put.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** When any value here changes between renders, the boundary resets. */
  resetKeys?: unknown[];
  /** Optional custom fallback; falls back to the default panel below. */
  fallback?: (reset: () => void) => ReactNode;
};

type State = { error: Error | null };

export function keysChanged(
  a: unknown[] | undefined,
  b: unknown[] | undefined,
): boolean {
  if (a === b) return false;
  if (!a || !b || a.length !== b.length) return true;
  return a.some((v, i) => !Object.is(v, b[i]));
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props) {
    if (this.state.error && keysChanged(prev.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Stdout/console only — no PII; the contracts subtree text is user data,
    // so we log the error/stack, not the component props.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);

    return (
      <div
        translate="no"
        className="rounded border border-red-200 bg-red-50 p-6 text-center"
      >
        <p className="text-sm font-medium text-red-800">Något gick fel i vyn.</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-red-700">
          Vyn kunde inte visas. Det kan bero på ett webbläsartillägg som
          översätter sidan automatiskt. Försök igen eller ladda om sidan.
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-4 rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800"
        >
          Försök igen
        </button>
      </div>
    );
  }
}
